import {
  type Auth0PluginOptions,
  type Auth0VueClientOptions,
  type Auth0VueClient
} from '@auth0/auth0-vue'
import { ref, type Plugin } from 'vue'
import {
  type RouteLocationNormalizedGeneric,
  type RouteLocationNormalizedLoadedGeneric
} from 'vue-router'
import { createAuth0 } from '../../composables/useAuth0'
import { AppError, ErrorCode } from '@gat-ship/error-handling'

let auth0: Auth0VueClient | null = null

interface Auth0Error {
  error: string
  error_description: string
}

/**
 * Start a fresh Auth0 login flow to recover from an unusable session (stale
 * refresh token, revoked user, tenant config change, etc.). Preserves the
 * current URL via `appState.target` so the user lands back where they were
 * after a successful re-auth.
 *
 * Safe to call even if Auth0 isn't initialized yet, or if we're outside a
 * browser (SSR, unit tests) — it no-ops in those cases.
 */
export function forceReauthentication(): void {
  if (!auth0 || typeof window === 'undefined') return
  const returnTo = `${window.location.pathname}${window.location.search}`
  try {
    const maybePromise = auth0.loginWithRedirect({
      appState: { target: returnTo }
    })
    // `loginWithRedirect` returns a Promise; if it rejects, fall back to a
    // hard navigation so the user still ends up at sign-in rather than stuck.
    if (
      maybePromise &&
      typeof (maybePromise as Promise<void>).catch === 'function'
    ) {
      ;(maybePromise as Promise<void>).catch(() => {
        window.location.href = '/sign-in'
      })
    }
  } catch {
    window.location.href = '/sign-in'
  }
}

/**
 * Fetch a fresh access token from Auth0. On any failure — stale refresh token,
 * network error, blocked user, tenant config drift — kick off a fresh login via
 * {@link forceReauthentication} and throw so the in-flight request fails
 * cleanly while the browser is already navigating away.
 *
 * Consumers never need to handle the thrown `AppError`; the navigation takes
 * care of the user-visible recovery. The throw exists only to unwind the
 * current promise chain.
 */
export async function getAuthorizationToken(): Promise<string> {
  if (!auth0) {
    throw new Error(
      'Auth0 is not initialized. Please call initializeAuth0 first.'
    )
  }
  try {
    return await auth0.getAccessTokenSilently()
  } catch (error) {
    const auth0Error = error as Auth0Error
    console.error('Token acquisition failed', auth0Error)
    forceReauthentication()
    throw new AppError({
      code: ErrorCode.REAUTHENTICATION_REQUIRED,
      message: 'Authorization failed. Please login again.',
      details: {
        error: auth0Error?.error,
        error_description: auth0Error?.error_description
      }
    })
  }
}

export const getAuth0Client = () => {
  if (!auth0) {
    throw new Error(
      'Auth0 is not initialized. Please call initializeAuth0 first.'
    )
  }
  return auth0
}

let isAuthenticatedAbsoluteTimeoutFn:
  | ReturnType<typeof setTimeout>
  | undefined = undefined
const DEFAULT_AUTH_TIMEOUT = 5000 // 5 seconds default timeout

export async function isAuthenticated(
  timeoutMs: number = DEFAULT_AUTH_TIMEOUT
) {
  return new Promise<boolean>((resolve, reject) => {
    clearTimeout(isAuthenticatedAbsoluteTimeoutFn)

    // This function will resolve after the specified timeout if the user is not authenticated.
    isAuthenticatedAbsoluteTimeoutFn = setTimeout(() => {
      console.warn(`Authentication check timed out after ${timeoutMs}ms`)
      reject(new Error(`Authentication check timed out after ${timeoutMs}ms`))
    }, timeoutMs)

    try {
      const auth0 = getAuth0Client()
      if (auth0.isLoading.value) {
        // Wait for the plugin to load.
        setTimeout(() => {
          resolve(isAuthenticated(timeoutMs))
        }, 200)
      } else {
        clearTimeout(isAuthenticatedAbsoluteTimeoutFn)
        resolve(auth0.isAuthenticated.value)
      }
    } catch (error) {
      clearTimeout(isAuthenticatedAbsoluteTimeoutFn)
      reject(error)
    }
  })
}

export const history = ref<string[]>([])

function getTargetUrl() {
  let result = '/'
  if (Array.isArray(history.value) && history.value.length > 0) {
    result = history.value[0] ?? '/'
  }
  if (
    result === '/sign-in' ||
    result === '/oauth2-logout' ||
    result === '/oauth2-callback'
  ) {
    return '/'
  }
  return result
}

export async function authRouteGuard(
  to: RouteLocationNormalizedGeneric,
  from: RouteLocationNormalizedLoadedGeneric
) {
  // const auth0 = getAuth0Client()
  const authenticated = await isAuthenticated()
  if (!authenticated && history.value.length < 1) {
    history.value.push(to.fullPath)
  }
  if (to.meta.allowPublicEntrance === true && to.name !== from.name) {
    return true
  }
  if (!authenticated) {
    getAuth0Client().loginWithRedirect({
      appState: {
        target: getTargetUrl()
      }
    })
    return false
  }
  return authenticated
}

/** Initialize auth0 client with session storage caching by default. Use cache to override. */
export const initializeAuth0 = (
  clientOptions: Auth0VueClientOptions,
  pluginOptions?: Auth0PluginOptions
) => {
  const auth0Plugin = createAuth0(
    { ...clientOptions, cacheLocation: 'localstorage' },
    pluginOptions
  )
  auth0 = auth0Plugin
  return auth0Plugin as unknown as Plugin
}
